import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Tooltip,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SignedInContext } from "../AppEnv";
import { SignedInDoctor } from "../auth/interfaces";
import { ALMSG_INTERNAL_ERR, ALMSG_TIMEOUT, PATIENTS_PATH } from "../env";
import { emptyPatient, Patient } from "../patients/interfaces";
import { getAllPatients, getPatient } from "../patients/utils";
import {
  EXAM_4PHOTOS_FULLNAME,
  EXAM_4PHOTOS_SLUGNAME,
  EXAM_AAII_FULLNAME,
  EXAM_AAII_SLUGNAME,
  EXAM_MAGNO_FULLNAME,
  EXAM_MAGNO_SLUGNAME,
  EXAM_PODO_FULLNAME,
  EXAM_PODO_SLUGNAME,
  EXAM_PODO_STIM_FULLNAME,
  EXAM_PODO_STIM_SLUGNAME,
  EXAM_VISION_FULLNAME,
  EXAM_VISION_SLUGNAME,
  EXAM_BALANCE_FULLNAME,
  EXAM_BALANCE_SLUGNAME,
  EXAM_HEAD_FULLNAME,
  EXAM_HEAD_SLUGNAME,
  EXAM_FUKUDA_FULLNAME,
  EXAM_FUKUDA_SLUGNAME,
  EXAM_ADAMS_FULLNAME,
  EXAM_ADAMS_SLUGNAME,
  EXAM_ROMBERG_FULLNAME,
  EXAM_ROMBERG_SLUGNAME,
  EXAM_RATE_FULLNAME,
  EXAM_RATE_SLUGNAME,
  EXAM_ANAM_SLUGNAME,
  EXAM_ANAM_FULLNAME,
  EXAM_TINETTI_FULLNAME,
  EXAM_TINETTI_SLUGNAME,
  EXAM_ELICOIDALE_FULLNAME,
  EXAM_ELICOIDALE_SLUGNAME,
} from "./exams_definitions_utils";
import { PosturalExamination } from "./interfaces";
import { createPosturalExamination } from "./utils";
import "../ButtonIcon.css";

import piani from "../images/icon_ex/4foto_icon.png";
import podo from "../images/podo_base.png";
import stim from "../images/icon_ex/podo_stim.png";
import vision from "../images/vision_base.png";
import magno from "../images/magno/48a.png";
import capo from "../images/capo/55a.png";
import fukuda from "../images/fukuda/62c.png";
import adams from "../images/adams/68a.png";
import aaii from "../images/aaii_base.png";
import bila from "../images/bl/bl1.png";
import rom from "../images/icon_ex/Romberg_base.png";
import progress from "../images/in_progress.png";
import anam from "../images/anam.png";
import rate from "../images/rate.png";
import tinetti from "../images/tinetti.png";
import elicoidale from "../images/elicoidale.png";

const layout = {
  labelCol: { span: 0 },
  wrapperCol: { span: 24 },
};

const AddExamModal = ({
  visible,
  setVisible,
  selectedPatient,
}: {
  visible: boolean;
  setVisible: Function;
  selectedPatient?: Patient;
}) => {
  const navigate = useNavigate();

  //for responsive
  const [width, setWidth] = useState(0);
  const handleResize = () => setWidth(window.innerWidth);

  useEffect(() => {
    //mount
    window.addEventListener("resize", handleResize);
    setWidth(window.innerWidth);
    //unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const signedInDoctor: SignedInDoctor =
    useContext(SignedInContext).signedInDoctor;
  const [form] = Form.useForm();

  const [allPatients, setAllPatients] = useState<Patient[]>([]);
  const [selPatient, setSelPatient] = useState(emptyPatient);
  const [selExam, setSelExam] = useState("");
  const [selExamName, setSelExamName] = useState("");
  const [iconEx, setIconEx] = useState("iconEx");

  const [matchingOptions, setMatchingOptions] = useState<Patient[]>([]);
  const [createError, setCreateError] = useState("");

  const [compiledBase, setCompiledBase] = useState(false);

  useEffect(() => {
    if (selectedPatient != undefined && selPatient != selectedPatient)
      setSelPatient(selectedPatient);
  }, [selPatient]);

  useEffect(() => {
    setCompiledBase(selPatient.name != "" && selExam != "");
  }, [selPatient, selExam]);

  useEffect(() => {
    if (selectedPatient == undefined)
      getAllPatients(signedInDoctor, true)
        .then((allPatients: Patient[]) => setAllPatients(allPatients))
        .catch(() => setAllPatients([]));
  }, [signedInDoctor]);

  const onChangeSelectPatient = (value: string) => {
    getPatient(signedInDoctor, parseInt(value)).then((patient: Patient) =>
      setSelPatient(patient)
    );
  };

  const onSearchSelectPatient = (value: string) => {
    if (value.length > 1) {
      //search matching patients
      setMatchingOptions(
        allPatients.filter(
          (p: Patient) =>
            p.name.toLowerCase().startsWith(value.toLowerCase()) ||
            p.surname.toLowerCase().startsWith(value.toLowerCase()) ||
            (p.name + " " + p.surname)
              .toLowerCase()
              .startsWith(value.toLowerCase())
        )
      );
    } else setMatchingOptions([]);
  };

  const createNewExam = () => {
    if (!compiledBase) setCreateError("Selezione non valida");
    else {
      createPosturalExamination(signedInDoctor, selPatient, selExam).then(
        (posturalExamination: PosturalExamination) => {
          if (
            posturalExamination.type == selExam &&
            posturalExamination.patientId == selPatient.id
          )
            navigate(
              "/app" +
                PATIENTS_PATH +
                "/" +
                selPatient.id +
                "/" +
                posturalExamination.examId +
                "?just_created=1"
            );
          // click row
          else setCreateError(ALMSG_INTERNAL_ERR);
        }
      );
    }
  };

  useEffect(() => {
    if (createError != "") setTimeout(() => setCreateError(""), ALMSG_TIMEOUT);
  }, [createError]);

  return (
    <>
      {width > 768 && (
        //Desktop
        <Modal
          title="Esegui un nuovo esame"
          centered
          open={visible}
          footer={false}
          onOk={() => setVisible(false)}
          onCancel={() => setVisible(false)}
          width={816}
          closable={false}
        >
          <Form {...layout} form={form} name="control-hooks" layout="vertical">
            <Form.Item className="centered" label="Paziente">
              {selectedPatient == undefined && (
                <Select
                  showSearch
                  placeholder="Cerca un paziente"
                  optionFilterProp="children"
                  onChange={onChangeSelectPatient}
                  onSearch={onSearchSelectPatient}
                >
                  {matchingOptions.map((p: Patient) => (
                    <Select.Option key={p.id} value={p.id} label={p.id}>
                      {p.name} {p.surname}
                    </Select.Option>
                  ))}
                </Select>
              )}
              {selectedPatient != undefined && (
                <Input
                  value={selectedPatient.name + " " + selectedPatient.surname}
                  disabled
                />
              )}
            </Form.Item>
            <Col className="space1"></Col>
            <Form.Item className="centered" label="Esame">
              <Row>
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_ANAM_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_ANAM_SLUGNAME)}
                    src={anam}
                  ></img>
                  <br />
                  {EXAM_ANAM_FULLNAME}
                </Col>
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_RATE_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_RATE_SLUGNAME)}
                    src={rate}
                  ></img>
                  <br />
                  {EXAM_RATE_FULLNAME}
                </Col>
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_4PHOTOS_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_4PHOTOS_SLUGNAME)}
                    src={piani}
                  ></img>
                  <br />
                  {EXAM_4PHOTOS_FULLNAME}
                </Col>
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_AAII_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_AAII_SLUGNAME)}
                    src={aaii}
                  ></img>{" "}
                  <br />
                  {EXAM_AAII_FULLNAME}
                </Col>
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_PODO_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_PODO_SLUGNAME)}
                    src={podo}
                  ></img>{" "}
                  <br />
                  {EXAM_PODO_FULLNAME}
                </Col>
              </Row>

              <Row className="rower">
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_VISION_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_VISION_SLUGNAME)}
                    src={vision}
                  ></img>{" "}
                  <br />
                  {EXAM_VISION_FULLNAME}
                </Col>
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_MAGNO_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_MAGNO_SLUGNAME)}
                    src={magno}
                  ></img>{" "}
                  <br />
                  {EXAM_MAGNO_FULLNAME}
                </Col>
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_HEAD_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_HEAD_SLUGNAME)}
                    src={capo}
                  ></img>{" "}
                  <br />
                  {EXAM_HEAD_FULLNAME}
                </Col>
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_FUKUDA_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_FUKUDA_SLUGNAME)}
                    src={fukuda}
                  ></img>{" "}
                  <br />
                  {EXAM_FUKUDA_FULLNAME}
                </Col>
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_ROMBERG_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_ROMBERG_SLUGNAME)}
                    src={rom}
                  ></img>
                  <br />
                  {EXAM_ROMBERG_FULLNAME}
                </Col>
              </Row>
              <Row className="rower">
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_ADAMS_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_ADAMS_SLUGNAME)}
                    src={adams}
                  ></img>
                  <br />
                  {EXAM_ADAMS_FULLNAME}
                </Col>
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_BALANCE_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_BALANCE_SLUGNAME)}
                    src={bila}
                  ></img>{" "}
                  <br />
                  {EXAM_BALANCE_FULLNAME}
                </Col>
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_TINETTI_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_TINETTI_SLUGNAME)}
                    src={tinetti}
                  ></img>
                  <br />
                  {EXAM_TINETTI_FULLNAME}
                </Col>
                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_PODO_STIM_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_PODO_STIM_SLUGNAME)}
                    src={stim}
                  ></img>{" "}
                  <br />
                  {EXAM_PODO_STIM_FULLNAME}
                </Col>

                <Col span={4} className="centered">
                  <img
                    className={
                      "iconEx" +
                      (selExam == EXAM_ELICOIDALE_SLUGNAME ? " iconExSel" : "")
                    }
                    onClick={() => setSelExam(EXAM_ELICOIDALE_SLUGNAME)}
                    src={elicoidale}
                  ></img>
                  <br />
                  {EXAM_ELICOIDALE_FULLNAME}
                </Col>
              </Row>
              <Col className="space1"></Col>
            </Form.Item>
            <Form.Item style={{ margin: "auto", textAlign: "right" }}>
              <Tooltip
                placement="top"
                title={!compiledBase ? "Seleziona paziente ed esame!" : ""}
              >
                <Button
                  className="perso"
                  type="dashed"
                  htmlType="submit"
                  disabled={!compiledBase}
                  style={{
                    background: compiledBase ? "#9edcc4" : "#a4b7b066",
                    color: "white",
                    margin: "2px",
                  }}
                  onClick={createNewExam}
                >
                  Crea Esame
                </Button>
              </Tooltip>
            </Form.Item>
            {createError && (
              <Form.Item>
                <Alert
                  message="Operazione non riuscita"
                  description={createError}
                  type="error"
                  showIcon
                />
              </Form.Item>
            )}
          </Form>
        </Modal>
      )}
      {width <= 768 && (
        //Mobile
        <Modal
          title="Esegui un nuovo esame"
          centered
          open={visible}
          footer={false}
          onOk={() => setVisible(false)}
          onCancel={() => setVisible(false)}
          width={816}
          closable={false}
        >
          <Form {...layout} form={form} name="control-hooks" layout="vertical">
            <Form.Item className="centered" label="Paziente">
              {selectedPatient == undefined && (
                <Select
                  showSearch
                  placeholder="Cerca un paziente"
                  optionFilterProp="children"
                  onChange={onChangeSelectPatient}
                  onSearch={onSearchSelectPatient}
                >
                  {matchingOptions.map((p: Patient) => (
                    <Select.Option key={p.id} value={p.id} label={p.id}>
                      {p.name} {p.surname}
                    </Select.Option>
                  ))}
                </Select>
              )}
              {selectedPatient != undefined && (
                <Input
                  value={selectedPatient.name + " " + selectedPatient.surname}
                  disabled
                />
              )}
            </Form.Item>
            <Col className="space1"></Col>
            <Form.Item className="centered" label="Esame">
              <Row>
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_ANAM_SLUGNAME ? " iconExSel-m " : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_ANAM_SLUGNAME);
                      setSelExamName(EXAM_ANAM_FULLNAME);
                    }}
                    src={anam}
                  ></img>
                </Col>
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_RATE_SLUGNAME ? " iconExSel-m " : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_RATE_SLUGNAME);
                      setSelExamName(EXAM_RATE_FULLNAME);
                    }}
                    src={rate}
                  ></img>
                </Col>
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_4PHOTOS_SLUGNAME ? " iconExSel-m " : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_4PHOTOS_SLUGNAME);
                      setSelExamName(EXAM_4PHOTOS_FULLNAME);
                    }}
                    src={piani}
                  ></img>
                </Col>
              </Row>

              <Row className="rower">
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_AAII_SLUGNAME ? " iconExSel-m " : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_AAII_SLUGNAME);
                      setSelExamName(EXAM_AAII_FULLNAME);
                    }}
                    src={aaii}
                  ></img>
                </Col>
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_PODO_SLUGNAME ? " iconExSel-m " : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_PODO_SLUGNAME);
                      setSelExamName(EXAM_PODO_FULLNAME);
                    }}
                    src={podo}
                  ></img>
                </Col>
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_VISION_SLUGNAME ? " iconExSel-m " : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_VISION_SLUGNAME);
                      setSelExamName(EXAM_VISION_FULLNAME);
                    }}
                    src={vision}
                  ></img>
                </Col>
              </Row>
              <Row className="rower">
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_MAGNO_SLUGNAME ? " iconExSel-m " : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_MAGNO_SLUGNAME);
                      setSelExamName(EXAM_MAGNO_FULLNAME);
                    }}
                    src={magno}
                  ></img>
                </Col>
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_HEAD_SLUGNAME ? " iconExSel-m " : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_HEAD_SLUGNAME);
                      setSelExamName(EXAM_HEAD_FULLNAME);
                    }}
                    src={capo}
                  ></img>
                </Col>
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_FUKUDA_SLUGNAME ? " iconExSel-m " : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_FUKUDA_SLUGNAME);
                      setSelExamName(EXAM_FUKUDA_FULLNAME);
                    }}
                    src={fukuda}
                  ></img>
                </Col>
              </Row>
              <Row className="rower">
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_ROMBERG_SLUGNAME ? " iconExSel-m " : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_ROMBERG_SLUGNAME);
                      setSelExamName(EXAM_ROMBERG_FULLNAME);
                    }}
                    src={rom}
                  ></img>
                </Col>
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_ADAMS_SLUGNAME ? " iconExSel-m " : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_ADAMS_SLUGNAME);
                      setSelExamName(EXAM_ADAMS_FULLNAME);
                    }}
                    src={adams}
                  ></img>
                </Col>
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_BALANCE_SLUGNAME ? " iconExSel-m " : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_BALANCE_SLUGNAME);
                      setSelExamName(EXAM_BALANCE_FULLNAME);
                    }}
                    src={bila}
                  ></img>
                </Col>
              </Row>

              <Row className="rower">
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_TINETTI_SLUGNAME ? " iconExSel-m " : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_TINETTI_SLUGNAME);
                      setSelExamName(EXAM_TINETTI_FULLNAME);
                    }}
                    src={tinetti}
                  ></img>
                </Col>
                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_PODO_STIM_SLUGNAME
                        ? " iconExSel-m "
                        : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_PODO_STIM_SLUGNAME);
                      setSelExamName(EXAM_PODO_STIM_FULLNAME);
                    }}
                    src={stim}
                  ></img>
                </Col>

                <Col span={8} className="centered">
                  <img
                    className={
                      "iconEx-m" +
                      (selExam == EXAM_ELICOIDALE_SLUGNAME
                        ? " iconExSel-m "
                        : "")
                    }
                    onClick={() => {
                      setSelExam(EXAM_ELICOIDALE_SLUGNAME);
                      setSelExamName(EXAM_ELICOIDALE_FULLNAME);
                    }}
                    src={elicoidale}
                  ></img>
                </Col>
              </Row>

              <Col className="space1"></Col>
            </Form.Item>
            <Form.Item style={{ margin: "auto", textAlign: "right" }}>
              <Tooltip
                placement="top"
                title={!compiledBase ? "Seleziona paziente ed esame!" : ""}
              >
                <span
                  style={{
                    position: "absolute",
                    left: "0",
                    marginLeft: "15px",
                  }}
                >
                  Esame: {selExamName}
                </span>
                <Button
                  className="perso"
                  type="dashed"
                  htmlType="submit"
                  disabled={!compiledBase}
                  style={{
                    background: compiledBase ? "#9edcc4" : "#a4b7b066",
                    color: "white",
                    margin: "2px",
                  }}
                  onClick={createNewExam}
                >
                  Crea Esame
                </Button>
              </Tooltip>
            </Form.Item>
            {createError && (
              <Form.Item>
                <Alert
                  message="Operazione non riuscita"
                  description={createError}
                  type="error"
                  showIcon
                />
              </Form.Item>
            )}
          </Form>
        </Modal>
      )}
    </>
  );
};

export default AddExamModal;

/* to save exam in standby

 <Col span={4} className="centered">
              <img
                title="Stiamo costruendo questo esame per te!"
                className="iconEx1"
                onClick={() => setSelExam("")}
                src={progress}
              ></img>
              <br />
              {"*Relazione Finale*"}
            </Col>

            /*

//Old style of choose

/*<Select
            placeholder="Seleziona esame"
            onChange={(value: string) => setSelExam(value)}
          >
            <Select.Option value="">-</Select.Option>
            <Select.Option value={EXAM_BALANCE_SLUGNAME}>
              {EXAM_BALANCE_FULLNAME}
            </Select.Option>
            <Select.Option value={EXAM_4PHOTOS_SLUGNAME}>
              {EXAM_4PHOTOS_FULLNAME}
            </Select.Option>
            <Select.Option value={EXAM_AAII_SLUGNAME}>
              {EXAM_AAII_FULLNAME}
            </Select.Option>
            <Select.Option value={EXAM_PODO_SLUGNAME}>
              {EXAM_PODO_FULLNAME}
            </Select.Option>
            <Select.Option value={EXAM_PODO_STIM_SLUGNAME}>
              {EXAM_PODO_STIM_FULLNAME}
            </Select.Option>
            <Select.Option value={EXAM_VISION_SLUGNAME}>
              {EXAM_VISION_FULLNAME}
            </Select.Option>
            <Select.Option value={EXAM_MAGNO_SLUGNAME}>
              {EXAM_MAGNO_FULLNAME}
            </Select.Option>
            <Select.Option value={EXAM_HEAD_SLUGNAME}>
              {EXAM_HEAD_FULLNAME}
            </Select.Option>
            <Select.Option value={EXAM_FUKUDA_SLUGNAME}>
              {EXAM_FUKUDA_FULLNAME}
            </Select.Option>
            <Select.Option value={EXAM_ADAMS_SLUGNAME}>
              {EXAM_ADAMS_FULLNAME}
            </Select.Option>
          </Select>*/
